import { createTheme } from "@mui/material/styles";
import * as colours from "./colours";

/**
 * Custom styles to be apply to all components within the app
 * @returns mui theme object to be reference in MUI ThemeProvider component
 */

const getCustomMuiTheme = (mode) => {
  let theme = {
    palette: {
      mode,
      ...(mode === "light"
        ? {
            primary: {
              main: colours.purple[1],
            },
            //dark purple
            primaryLight: {
              main: "#5315BC",
            },
            //pink
            secondary: {
              main: colours.green[1],
            },
            //blue
            info: {
              main: "#1E7DFF",
            },
            //green
            success: {
              main: "#028931",
            },
            //red
            error: {
              main: "#CE0C14",
            },
            //background color
            background: {
              main: "#f4f2f8",
            },
          }
        : {
            // temp colors from light theme start here

            //dark purple
            primaryLight: {
              main: "#5315BC",
            },
            //blue
            info: {
              main: "#1E7DFF",
            },
            //green
            success: {
              main: "#028931",
            },
            //red
            error: {
              main: "#CE0C14",
            },

            // actual dark theme colors start here
            // white
            primary: {
              main: "#FFF",
            },
            // pink
            secondary: {
              main: "#d938fb",
            },
            // dark purple
            background: {
              main: "#120739",
            },
          }),
    },
  };

  theme = {
    ...theme,
    typography: {
      fontFamily: "Poppins, sans-serif",
      h1: {
        fontSize: "1.75rem",
        fontWeight: 800,
      },
      h2: {
        fontSize: "1.25rem",
        fontWeight: 800,
      },
      h3: {
        fontSize: "1rem",
        fontWeight: 900,
      },
      h4: {
        fontSize: "0.875rem",
        fontWeight: 700,
      },
      body1: {
        fontSize: "0.75rem",
        fontWeight: 500,
        lineHeight: 1,
      },
      body2: {
        fontSize: "1rem",
        lineHeight: 1,
      },
      subtitle1: {
        fontSize: "1rem",
        fontWeight: 500,
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.85rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.80rem",
        },
      },
      subtitle2: {
        fontSize: "1rem",
        fontStyle: "italic",
        "@media (max-width:1280px) and (min-width:744px)": {
          fontSize: "0.85rem",
        },
        "@media (max-width:743px) and (min-width:300px)": {
          fontSize: "0.80rem",
        },
      },
      error: {
        color: theme.palette.error.main,
        fontSize: ".75rem",
        fontFamily: "Poppins, sans-serif",
      },
      error2: {
        color: theme.palette.error.main,
        fontSize: "1.5rem",
        fontFamily: "Poppins, sans-serif",
      },
      success: {
        color: theme.palette.success.main,
        fontSize: "1rem",
        fontFamily: "Poppins, sans-serif",
      },
      success2: {
        color: theme.palette.success.main,
        fontSize: "1.5rem",
        fontFamily: "Poppins, sans-serif",
      },
      overSize: {
        fontSize: "5rem",
        fontWeight: 600,
      },
      overSize2: {
        fontSize: "3rem",
        fontWeight: 600,
        fontFamily: "Poppins, sans-serif",
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            color: colours.text.primary,
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: {
            "&:hover:not(.Mui-disabled)": {
              cursor: "pointer",
            },
          },
        },
      },
      MuiDataGrid: {
        styleOverrides: {
          root: {
            fontSize: "1rem",
            color: theme.palette.primary.main,
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: "#f0ecec",
            color: theme.palette.primary.main,
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            marginBottom: "1rem",
            width: "100%",
            color: theme.palette.primary.main,
          },
        },
      },
      MuiAutocomplete: {
        styleOverrides: {
          root: {
            backgroundColor: "#fff",
            borderRadius: "10px",
            boxShadow: "-5px 5px 6px 1px #E6E6E6",
            width: "92%",
          },
          option: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            boxShadow: "none",
          },
          contained: {
            background: colours.green[1],
            borderRadius: "10px",
            padding: "0.375rem",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.875rem",
            color: "#FFF",
            "&:hover": {
              boxShadow: "none",
              background: colours.green[2],
            },
          },
          outlined: {
            borderRadius: "10px",
            padding: "0.25rem 2.5rem",
            fontWeight: 700,
            textTransform: "none",
            fontSize: "0.875rem",
            color: theme.palette.primary.main,
            borderColor: theme.palette.primary.main,
            "&:hover": {
              borderColor: theme.palette.primary.main,
              border: `0.2rem solid ${theme.palette.primary.main}`,
            },
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1rem",
          },
        },
      },
      MuiFormControlLabel: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1rem",
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            display: "flex",
            flexDirection: "column",
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
          notchedOutline: {
            borderColor: theme.palette.primary.main,
          },
        },
      },
      MuiInput: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "1.2rem",
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            fontSize: "2rem",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: theme.palette.primary.main,
            transform: "scale(1.5)",
            "&:hover:not(.Mui-disabled)": {
              cursor: "pointer",
            },
          },
        },
      },
      MuiTreeItem: {
        styleOverrides: {
          content: {
            padding: "8px",
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            fontSize: "0.75rem",
            fontWeight: 600,
            boxShadow: `0px 3px 30px ${colours.boxShadow}`,
            "& fieldset.MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
          },
        },
      },
    },
  };

  return createTheme(theme);
};

export default getCustomMuiTheme;
